import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Brain,
  Calculator,
  Users,
  Clock,
  Moon,
  Calendar,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Download,
  Send,
  Loader2,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Check,
  X,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const CentrePaieIA = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // AI Chat
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  
  // Period navigation
  const getCurrentPeriod = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [periode, setPeriode] = useState(getCurrentPeriod());

  const formatPeriode = (p) => {
    const [year, month] = p.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const navigatePeriod = (direction) => {
    const [year, month] = periode.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    if (newMonth < 1) { newMonth = 12; newYear--; }
    setPeriode(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, agentsRes] = await Promise.all([
        api.get(`/centre-paie/stats/${periode}`),
        api.get(`/centre-paie/agents/${periode}`)
      ]);
      setStats(statsRes.data);
      setAgents(agentsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les données', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [periode, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAskAI = async (question = aiQuestion) => {
    if (!question.trim()) return;
    setAiLoading(true);
    try {
      const response = await api.post('/centre-paie/ai/chat', {
        question,
        context: selectedAgent ? `Agent: ${selectedAgent.agentName}, Heures: ${selectedAgent.heuresTotal}h` : null
      });
      setAiResponse(response.data.response);
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Erreur IA:', error);
      toast({ title: 'Erreur IA', description: 'Impossible de contacter l\'assistant', variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const startEdit = (agent) => {
    setEditingAgent(agent.agentId);
    setEditValues({
      primePanier: agent.primePanier || 0,
      primeExceptionnelle: agent.primeExceptionnelle || 0,
      primeChien: agent.primeChien || 0,
      indemniteKm: agent.indemniteKm || 0,
      indemniteNavigo: agent.indemniteNavigo || 0,
      acompte: agent.acompte || 0,
      absenceHeures: agent.absenceHeures || 0
    });
  };

  const cancelEdit = () => {
    setEditingAgent(null);
    setEditValues({});
  };

  const saveEdit = async (agentId) => {
    try {
      await api.put(`/centre-paie/agents/${periode}/${agentId}/variables`, editValues);
      toast({ title: 'Enregistré', description: 'Variables mises à jour' });
      setEditingAgent(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const validateAgent = async (agentId) => {
    try {
      await api.post(`/centre-paie/agents/${periode}/${agentId}/validate`);
      toast({ title: 'Validé', description: 'Variables de paie validées' });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de valider', variant: 'destructive' });
    }
  };

  const exportData = async () => {
    try {
      const response = await api.post(`/centre-paie/export/${periode}`);
      toast({ title: 'Export réussi', description: response.data.message });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur export', description: error.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Centre Paie & IA
              </h1>
              <p className="text-slate-400">Gestion intelligente des variables de paie</p>
            </div>
          </div>
          
          {/* Period Navigation */}
          <div className="flex items-center gap-3 bg-slate-800 rounded-xl px-4 py-2">
            <Button variant="ghost" size="sm" onClick={() => navigatePeriod(-1)} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-lg font-semibold text-white min-w-[180px] text-center">
              {formatPeriode(periode)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => navigatePeriod(1)} className="text-slate-400 hover:text-white">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats?.totalAgents || 0}</p>
                <p className="text-xs text-slate-400">Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats?.totalHeures || 0}h</p>
                <p className="text-xs text-slate-400">Heures totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Moon className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats?.totalHeuresNuit || 0}h</p>
                <p className="text-xs text-slate-400">Heures nuit</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats?.totalHeuresDimanche || 0}h</p>
                <p className="text-xs text-slate-400">H. Dimanche</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats?.agentsValides || 0}</p>
                <p className="text-xs text-slate-400">Validés</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-white">{(stats?.masseSalarialeBrute || 0).toLocaleString()}€</p>
                <p className="text-xs text-slate-400">Masse brute</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Table */}
        <div className="lg:col-span-2">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="border-b border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-blue-400" />
                  Variables de paie par agent
                </CardTitle>
                <Button 
                  onClick={exportData}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={!agents.some(a => a.statut === 'valide')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporter
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="text-left p-3 text-slate-300 font-medium">Agent</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Heures</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Nuit</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Dim.</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Sup 25%</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Primes</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Statut</th>
                      <th className="text-center p-3 text-slate-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center p-8 text-slate-400">
                          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400 opacity-70" />
                          <p className="text-base font-medium text-slate-300 mb-2">Aucune garde planifiee pour {formatPeriode(periode)}</p>
                          <p className="text-sm text-slate-500 max-w-md mx-auto">
                            Les donnees de paie sont calculees automatiquement a partir des gardes (shifts) enregistrees dans le module Planning.
                            Creez des gardes pour vos agents dans <span className="text-blue-400">Planning Agents</span> pour voir les donnees ici.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      agents.map((agent) => (
                        <tr 
                          key={agent.agentId} 
                          className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors cursor-pointer
                            ${selectedAgent?.agentId === agent.agentId ? 'bg-blue-900/30' : ''}`}
                          onClick={() => setSelectedAgent(agent)}
                        >
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                                {agent.agentName?.charAt(0) || 'A'}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">{agent.agentName}</p>
                                <p className="text-slate-400 text-xs">{agent.nbShifts} shifts</p>
                              </div>
                            </div>
                          </td>
                          <td className="text-center p-3 text-white font-semibold">{agent.heuresTotal}h</td>
                          <td className="text-center p-3 text-purple-400">{agent.heuresNuit}h</td>
                          <td className="text-center p-3 text-orange-400">{agent.heuresDimanche}h</td>
                          <td className="text-center p-3 text-yellow-400">{agent.heuresSup25}h</td>
                          <td className="text-center p-3 text-green-400">
                            {(agent.primePanier + agent.primeExceptionnelle + agent.primeChien).toFixed(0)}€
                          </td>
                          <td className="text-center p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium
                              ${agent.statut === 'valide' ? 'bg-emerald-900/50 text-emerald-400' : 
                                agent.statut === 'exporte' ? 'bg-blue-900/50 text-blue-400' :
                                'bg-yellow-900/50 text-yellow-400'}`}>
                              {agent.statut === 'valide' ? '✓ Validé' : 
                               agent.statut === 'exporte' ? '📤 Exporté' : '⏳ Brouillon'}
                            </span>
                          </td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); startEdit(agent); }}
                                className="text-slate-400 hover:text-white"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              {agent.statut !== 'valide' && agent.statut !== 'exporte' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); validateAgent(agent.agentId); }}
                                  className="text-emerald-400 hover:text-emerald-300"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Assistant Panel */}
        <div className="space-y-6">
          {/* Agent Detail Card */}
          {selectedAgent && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700 pb-3">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  {selectedAgent.agentName}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {editingAgent === selectedAgent.agentId ? (
                  // Edit mode
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-400">Prime Panier</label>
                        <Input
                          type="number"
                          value={editValues.primePanier}
                          onChange={(e) => setEditValues({...editValues, primePanier: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Prime Except.</label>
                        <Input
                          type="number"
                          value={editValues.primeExceptionnelle}
                          onChange={(e) => setEditValues({...editValues, primeExceptionnelle: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Indemnité KM</label>
                        <Input
                          type="number"
                          value={editValues.indemniteKm}
                          onChange={(e) => setEditValues({...editValues, indemniteKm: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Navigo</label>
                        <Input
                          type="number"
                          value={editValues.indemniteNavigo}
                          onChange={(e) => setEditValues({...editValues, indemniteNavigo: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Acompte</label>
                        <Input
                          type="number"
                          value={editValues.acompte}
                          onChange={(e) => setEditValues({...editValues, acompte: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400">Abs. (h)</label>
                        <Input
                          type="number"
                          value={editValues.absenceHeures}
                          onChange={(e) => setEditValues({...editValues, absenceHeures: parseFloat(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => saveEdit(selectedAgent.agentId)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                        <Check className="w-4 h-4 mr-1" /> Enregistrer
                      </Button>
                      <Button onClick={cancelEdit} variant="outline" className="border-slate-600">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-slate-700/50 rounded p-2">
                        <span className="text-slate-400">Heures base</span>
                        <p className="text-white font-semibold">{selectedAgent.heuresTotal}h</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2">
                        <span className="text-slate-400">Jours travaillés</span>
                        <p className="text-white font-semibold">{selectedAgent.nbJoursTravailles}</p>
                      </div>
                      <div className="bg-purple-900/30 rounded p-2">
                        <span className="text-purple-300">H. Nuit (+10%)</span>
                        <p className="text-purple-200 font-semibold">{selectedAgent.heuresNuit}h</p>
                      </div>
                      <div className="bg-orange-900/30 rounded p-2">
                        <span className="text-orange-300">H. Dim (+10%)</span>
                        <p className="text-orange-200 font-semibold">{selectedAgent.heuresDimanche}h</p>
                      </div>
                      <div className="bg-yellow-900/30 rounded p-2">
                        <span className="text-yellow-300">H. Sup 25%</span>
                        <p className="text-yellow-200 font-semibold">{selectedAgent.heuresSup25}h</p>
                      </div>
                      <div className="bg-red-900/30 rounded p-2">
                        <span className="text-red-300">H. Sup 50%</span>
                        <p className="text-red-200 font-semibold">{selectedAgent.heuresSup50}h</p>
                      </div>
                    </div>
                    {selectedAgent.statut === 'valide' && (
                      <div className="bg-emerald-900/30 border border-emerald-700 rounded p-2 text-sm">
                        <p className="text-emerald-400">✓ Validé par {selectedAgent.validePar}</p>
                        <p className="text-emerald-300/70 text-xs">{selectedAgent.valideLe}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Chat */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700">
            <CardHeader className="border-b border-slate-700 pb-3">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Assistant IA Paie
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {aiResponse && (
                <div className="bg-slate-700/50 rounded-lg p-3 text-sm text-slate-200 max-h-48 overflow-y-auto">
                  <MessageSquare className="w-4 h-4 text-blue-400 inline mr-2" />
                  {aiResponse}
                </div>
              )}
              
              {aiSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {aiSuggestions.map((s, i) => (
                    <Button 
                      key={i} 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setAiQuestion(s); handleAskAI(s); }}
                      className="text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Posez une question sur la paie..."
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAskAI()}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                />
                <Button 
                  onClick={() => handleAskAI()}
                  disabled={aiLoading || !aiQuestion.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>

              {/* Quick Questions */}
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleAskAI("Comment calculer les heures supplémentaires ?")} 
                  className="text-xs text-slate-400 hover:text-white justify-start"
                >
                  💡 Comment calculer les heures sup ?
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleAskAI("Quelle est la majoration pour les heures de nuit ?")} 
                  className="text-xs text-slate-400 hover:text-white justify-start"
                >
                  🌙 Majoration heures de nuit ?
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleAskAI("Quelles sont les primes obligatoires en sécurité privée ?")} 
                  className="text-xs text-slate-400 hover:text-white justify-start"
                >
                  💰 Primes obligatoires sécurité ?
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CentrePaieIA;
